import React from 'react';
import { Building, Hash, Settings, Mail, Calendar, Send, Tag } from 'lucide-react';
import { formatJobRevNumber } from '../utils/projectFormatting';

const OperationsDashboardCard = ({ project, onClick, emailTracking }) => {
  const formatJobNumber = () => {
    return formatJobRevNumber(project?.jobNumber, project?.rev, "N/A");
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--dark-elevated)',
        border: '1px solid var(--gray-600)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-4)',
        cursor: 'pointer',
        transition: 'all var(--transition-fast)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--gray-500)';
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = 'var(--shadow-md)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--gray-600)';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Card Header */}
      <div
        style={{
          marginBottom: 'var(--space-3)',
          paddingBottom: 'var(--space-3)',
          borderBottom: '1px solid var(--gray-700)',
        }}
      >
        <h3
          style={{
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-semibold)',
            color: 'var(--gray-50)',
            margin: '0 0 var(--space-2) 0',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={project.projectName}
        >
          {project.projectName || 'Unnamed Project'}
        </h3>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-semibold)',
            color: 'var(--gray-50)',
            backgroundColor: 'var(--gray-700)',
            padding: '4px 10px',
            borderRadius: 'var(--radius-md)',
            width: 'fit-content',
          }}
        >
          <Hash size={14} style={{ color: 'var(--gray-400)' }} />
          <span>{formatJobNumber()}</span>
        </div>
      </div>

      {/* Card Body */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
        }}
      >
        {project.clientName && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-2)',
            }}
          >
            <Building
              size={14}
              style={{
                flexShrink: 0,
                marginTop: '2px',
                color: 'var(--gray-400)',
              }}
            />
            <span
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-300)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={project.clientName}
            >
              {project.clientName}
            </span>
          </div>
        )}

        {project.projectTypeName && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-2)',
            }}
          >
            <Tag
              size={14}
              style={{
                flexShrink: 0,
                marginTop: '2px',
                color: 'var(--gray-400)',
              }}
            />
            <span
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-300)',
              }}
            >
              {project.projectTypeName}
            </span>
          </div>
        )}

        {project.modelingStatus && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-2)',
            }}
          >
            <Settings
              size={14}
              style={{
                flexShrink: 0,
                marginTop: '2px',
                color: 'var(--gray-400)',
              }}
            />
            <span
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-300)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={project.modelingStatus}
            >
              {project.modelingStatus}
            </span>
          </div>
        )}

        {/* Show initial email from On Hold dashboard */}
        {project.holdEmailSentDate && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-2)',
            }}
          >
            <Send
              size={14}
              style={{
                flexShrink: 0,
                marginTop: '2px',
                color: 'var(--cta-blue)',
              }}
            />
            <span
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-300)',
              }}
              title="Initial email sent from On Hold dashboard"
            >
              Initial: {formatDate(project.holdEmailSentDate)}
            </span>
          </div>
        )}

        {/* Show recurring email tracking */}
        {emailTracking && (
          <>
            {emailTracking.last_email_sent_date && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--space-2)',
                }}
              >
                <Mail
                  size={14}
                  style={{
                    flexShrink: 0,
                    marginTop: '2px',
                    color: 'var(--gray-400)',
                  }}
                />
                <span
                  style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--gray-300)',
                  }}
                  title="Last recurring email sent"
                >
                  Last: {formatDate(emailTracking.last_email_sent_date)}
                </span>
              </div>
            )}
            {emailTracking.next_reminder_date && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--space-2)',
                }}
              >
                <Calendar
                  size={14}
                  style={{
                    flexShrink: 0,
                    marginTop: '2px',
                    color: 'var(--gray-400)',
                  }}
                />
                <span
                  style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--gray-300)',
                  }}
                  title="Next reminder date"
                >
                  Next: {formatDate(emailTracking.next_reminder_date)}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OperationsDashboardCard;